import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { customersAPI, projectsAPI, showFlashMessage, showErrorModal } from '../services/api';
import { toast } from 'sonner';
import { DollarSign, Clock } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import CreateInformationTab from '../components/project/CreateInformationTab';
import CreatePersonnelTab from '../components/project/CreatePersonnelTab';
import CreateExpensesTab from '../components/project/CreateExpensesTab';
import { readResponse, readResponseJSON } from '../utils/fetchSafe';
import { getJobTypeCommission } from '../utils/jobTypeCommission';

// Territory mapping: city → franchise number
const CITY_TO_FRANCHISE_NUMBER = {
  'philadelphia': '10090',
  'philly': '10090',
  'langhorne': '10089',
  'bensalem': '10089',
  'feasterville': '10090',
  'bristol': '10089',
  'levittown': '10089',
  'newtown': '10089',
  'doylestown': '10089',
};

const getTerritoryFromCity = (city, franchiseList) => {
  if (!city || !franchiseList?.length) return null;
  const number = CITY_TO_FRANCHISE_NUMBER[city.toLowerCase().trim()];
  if (!number) return null;
  const franchise = franchiseList.find(f => f.number === number);
  return franchise ? `${franchise.name} (${franchise.number})` : null;
};

const CreateProject = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('information');
  
  // Customer data
  const [customers, setCustomers] = useState([]);
  const [loadingCustomers, setLoadingCustomers] = useState(true);
  const [customerAddresses, setCustomerAddresses] = useState([]);
  
  // Basics
  const [selectedCustomerId, setSelectedCustomerId] = useState('');
  const [phone, setPhone] = useState('');
  const [territory, setTerritory] = useState('');
  const [projectType, setProjectType] = useState('');
  const [address, setAddress] = useState('');
  const [projectSource, setProjectSource] = useState('');
  
  // Key Dates
  const [fnolDate, setFnolDate] = useState('');
  const [estimateDate, setEstimateDate] = useState('');
  const [startDate, setStartDate] = useState('');
  const [completionDate, setCompletionDate] = useState('');
  
  // Financial
  const [preTaxTotalValue, setPreTaxTotalValue] = useState('');
  const [placeholder, setPlaceholder] = useState(false);
  const [paymentType, setPaymentType] = useState('');
  const [claimNumber, setClaimNumber] = useState('');
  const [invoiceNumber, setInvoiceNumber] = useState('');
  const [paidInFull, setPaidInFull] = useState(false);
  const [mgmtBonusPaid, setMgmtBonusPaid] = useState('');
  const [projectPaidDate, setProjectPaidDate] = useState('');
  
  // Project Options
  const [status, setStatus] = useState('');
  const [restorationOpp, setRestorationOpp] = useState('');
  const [nonCommissionProject, setNonCommissionProject] = useState(false);
  const [authorizationSigned, setAuthorizationSigned] = useState(false);
  const [uploadRequired, setUploadRequired] = useState(false);
  
  const [saving, setSaving] = useState(false);
  const savingRef = React.useRef(false);
  const [attempted, setAttempted] = useState(false);
  const [notes, setNotes] = useState('');
  const [noteUrgent, setNoteUrgent] = useState(false);
  const [savedNotesList, setSavedNotesList] = useState([]);
  
  // Key Personnel state
  const [selectedProjectLead, setSelectedProjectLead] = useState('');
  const [selectedEstimator, setSelectedEstimator] = useState('');
  const [estimatorAction, setEstimatorAction] = useState(false);
  const [selectedMarketingRep, setSelectedMarketingRep] = useState('');
  const [selectedOfficeContact, setSelectedOfficeContact] = useState('');
  const [officeAction, setOfficeAction] = useState(false);
  const [pmRows, setPmRows] = useState([{ employee: '', percent: '100' }]);
  const [createdProjectId, setCreatedProjectId] = useState(null);

  // Expenses state
  const [expenseRows, setExpenseRows] = useState([{ expense: '', date: '', memo: '', amount: '', percent: '100' }]);
  const [savedExpenses, setSavedExpenses] = useState([]);
  const [expenseOptions, setExpenseOptions] = useState([]);
  const [expenseSort, setExpenseSort] = useState({ column: null, direction: 'asc' });
  const [expenseSearch, setExpenseSearch] = useState('');

  // Dynamic personnel lists
  const [projectLeads, setProjectLeads] = useState([]);
  const [estimators, setEstimators] = useState([]);
  const [marketingReps, setMarketingReps] = useState([]);
  const [officeContacts, setOfficeContacts] = useState([]);
  const [productionEmployees, setProductionEmployees] = useState([]);
  const [franchises, setFranchises] = useState([]);
  const [projectSources, setProjectSources] = useState([]);
  const [projectTypes, setProjectTypes] = useState([]);
  // Map of Job Type name -> commission % (drives the Expenses/Profit Margin boxes)
  const [typeCommissionMap, setTypeCommissionMap] = useState({});

  useEffect(() => {
    fetchCustomers();
    fetchPersonnel();
    fetchExpenseOptions();
    fetchFranchises();
    fetchProjectTypes();
    fetchProjectSources();
  }, []);

  const fetchProjectTypes = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/project-types`);
      const { data } = await readResponse(res);
      const map = {};
      const types = (data.project_types || []).map(t => t.name).filter(Boolean);
      (data.project_types || []).forEach(t => {
        const pct = parseFloat(t.commission);
        if (!isNaN(pct)) map[t.name] = pct;
      });
      setProjectTypes(types);
      setTypeCommissionMap(map);
    } catch { /* silent */ }
  };

  const fetchProjectSources = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/project-sources`);
      const { data } = await readResponse(res);
      setProjectSources((data.sources || []).map(source => source.name));
    } catch {
      toast.error('Failed to load project sources');
    }
  };

  const fetchPersonnel = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    const roles = [
      { role: 'Project Lead', setter: setProjectLeads },
      { role: 'Estimator', setter: setEstimators },
      { role: 'Marketing', setter: setMarketingReps },
      { role: 'Office', setter: setOfficeContacts },
      { role: 'Production', setter: setProductionEmployees },
    ];
    try {
      const results = await Promise.all(
        roles.map(r => fetch(`${API_URL}/api/employees?active=true&role=${encodeURIComponent(r.role)}`).then(readResponseJSON))
      );
      results.forEach((data, i) => roles[i].setter(data.employees || []));
    } catch { /* silent */ }
  };

  const fetchFranchises = async () => {
    const API_URL = process.env.REACT_APP_BACKEND_URL;
    try {
      const res = await fetch(`${API_URL}/api/franchise-locations`);
      const { data } = await readResponse(res);
      setFranchises(data.franchises || []);
    } catch {}
  };

  const fetchCustomers = async () => {
    try {
      setLoadingCustomers(true);
      const data = await customersAPI.getAll();
      setCustomers(data || []);
    } catch (error) {
      console.error('Error fetching customers:', error);
      toast.error('Failed to load customers');
    } finally {
      setLoadingCustomers(false);
    }
  };

  const fetchExpenseOptions = async () => {
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const res = await fetch(`${API_URL}/api/project-expenses`);
      const { data } = await readResponse(res);
      setExpenseOptions(data.expenses || []);
    } catch { /* silent */ }
  };

  const handleAddExpenseRow = () => {
    setExpenseRows(prev => [...prev, { expense: '', date: '', memo: '', amount: '', percent: '100' }]);
  };

  const handleRemoveExpenseRow = (index) => {
    setExpenseRows(prev => prev.filter((_, i) => i !== index));
  };

  const handleExpenseRowChange = (index, field, value) => {
    setExpenseRows(prev => prev.map((row, i) => i === index ? { ...row, [field]: value } : row));
  };

  const handleDeleteSavedExpense = (index) => {
    setSavedExpenses(prev => prev.filter((_, i) => i !== index));
  };

  const handleSaveExpenseRow = (index) => {
    const row = expenseRows[index];
    if (!row.expense && !row.date && !row.memo && !row.amount) return;
    setSavedExpenses(prev => [...prev, {
      expense: row.expense,
      date: row.date,
      memo: row.memo,
      amount: parseFloat(String(row.amount || '0').replace(/[$,]/g, '')) || 0,
      percent: row.percent || '100',
      last_updated: new Date().toLocaleString('en-US')
    }]);
    setExpenseRows(prev => prev.map((r, i) => i === index ? { expense: '', date: '', memo: '', amount: '', percent: '100' } : r));
    toast.success('Expense saved');
  };

  const handleCustomerChange = (customerId) => {
    setSelectedCustomerId(customerId);
    
    if (!customerId) {
      setCustomerAddresses([]);
      setAddress('');
      return;
    }
    
    const customer = customers.find(c => c.customer_id === customerId);
    
    // Auto-populate phone
    if (customer) {
      setPhone(customer.primary_phone || '');
    }
    
    if (customer && customer.addresses && customer.addresses.length > 0) {
      setCustomerAddresses(customer.addresses);
      const currentAddress = customer.addresses.find(a => a.current) || customer.addresses[0];
      if (currentAddress) {
        const addressString = `${currentAddress.street_address || ''}, ${currentAddress.city || ''}, ${currentAddress.state || ''} ${currentAddress.zip || ''}`.trim();
        setAddress(addressString);
        const city = currentAddress.city;
        const newTerritory = getTerritoryFromCity(city, franchises);
        if (newTerritory && !territory) {
          setTerritory(newTerritory);
          toast.success(`Territory updated to ${newTerritory}`);
        }
      }
    } else {
      setCustomerAddresses([]);
      setAddress('');
    }
  };

  const handleAddressChange = (addressString) => {
    setAddress(addressString);
    const matchingAddress = customerAddresses.find(a => {
      const fullAddress = `${a.street_address || ''}, ${a.city || ''}, ${a.state || ''} ${a.zip || ''}`.trim();
      return fullAddress === addressString;
    });
    if (matchingAddress && matchingAddress.city) {
      const newTerritory = getTerritoryFromCity(matchingAddress.city, franchises);
      if (newTerritory && !territory) {
        setTerritory(newTerritory);
        toast.success(`Territory updated to ${newTerritory}`);
      }
    }
  };

  const getCustomerDisplayName = (customer) => {
    const lastName = customer.primary_last_name || customer.last_name || '';
    const firstName = customer.primary_first_name || customer.first_name || '';
    const company = customer.company || '';
    const name = lastName && firstName ? `${lastName}, ${firstName}` : (lastName || firstName);
    if (customer.type === 'Commercial' && company) {
      return name ? `${company} - ${name}` : company;
    }
    if (name) {
      return company ? `${name} - ${company}` : name;
    }
    return company || customer.customer_id || 'Unknown';
  };

  const handleSaveNote = () => {
    if (!notes.trim()) return;
    const newNote = {
      created_by: user?.name || user?.email || 'Unknown',
      created_on: new Date().toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase(),
      text: notes.trim(),
      urgent: noteUrgent
    };
    setSavedNotesList(prev => [newNote, ...prev]);
    setNotes('');
    setNoteUrgent(false);
    toast.success('Note saved');
  };

  const saveProject = async () => {
    // Re-entrancy guard: even if the `saving` state hasn't propagated yet,
    // this synchronous ref prevents a rapid double-click from creating
    // two identical projects.
    if (savingRef.current) return null;
    savingRef.current = true;
    setAttempted(true);
    const missing = [];
    if (!selectedCustomerId) missing.push('Customer');
    if (!projectType) missing.push('Type');
    if (!address) missing.push('Address');
    if (!projectSource) missing.push('Project Source');
    if (!fnolDate) missing.push('FNOL Date');
    if (!status) missing.push('Status');
    if (['Accounts Receivable', 'Closed', 'Completed'].includes(status) && !completionDate) missing.push('Completion Date');
    if ((status === 'Closed' || status === 'Completed') && !projectPaidDate) missing.push('Project Paid Date');

    if (missing.length > 0) {
      showErrorModal(`Please fill in required fields: ${missing.join(', ')}`);
      savingRef.current = false;
      return null;
    }

    const customer = customers.find(c => c.customer_id === selectedCustomerId);
    const customerName = customer ? getCustomerDisplayName(customer) : '';

    const projectData = {
      customer: customerName,
      customer_id: selectedCustomerId,
      phone: phone,
      email: customer?.email || '',
      territory: territory,
      job_type: projectType,
      address: address,
      project_source: projectSource,
      fnol_date: fnolDate,
      estimate_date: estimateDate,
      start_date: startDate,
      completion_date: completionDate,
      finish_date: completionDate,
      pre_tax_total_value: preTaxTotalValue,
      job_payment: paymentType,
      claim_number: claimNumber,
      invoice_number: invoiceNumber,
      mgmt_bonus_paid: mgmtBonusPaid,
      project_paid_date: projectPaidDate,
      restoration_opp: restorationOpp,
      non_commission_project: nonCommissionProject,
      authorization_signed: authorizationSigned,
      upload_required: uploadRequired,
      job_status: status,
      notes_list: [
        ...savedNotesList,
        ...(notes.trim() ? [{
          created_by: user?.name || user?.email || 'Unknown',
          created_on: new Date().toLocaleString('en-US', { month: 'numeric', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit', hour12: true }).toLowerCase(),
          text: notes.trim(),
          urgent: noteUrgent
        }] : [])
      ],
      project_lead: selectedProjectLead,
      estimator_contact: selectedEstimator,
      estimator_action: estimatorAction,
      marketing_rep: selectedMarketingRep,
      office_contact: selectedOfficeContact,
      office_action: officeAction,
      production_manager: pmRows[0]?.employee || '',
      pm_commission_percent: pmRows[0]?.percent || '100',
      pm_rows: pmRows.filter(r => r.employee),
      expenses_list: [
        ...savedExpenses,
        ...expenseRows.filter(e => e.expense).map(e => ({
          expense: e.expense,
          date: e.date,
          memo: e.memo,
          amount: parseFloat(String(e.amount || '0').replace(/[$,]/g, '')) || 0,
          percent: e.percent || '100',
          last_updated: new Date().toLocaleString('en-US')
        }))
      ],
      status: status === 'Accounts Receivable' ? 'ar' : 
              status === 'Closed' ? 'closed' : 
              status === 'Dead' ? 'dead' : 'open'
    };

    setSaving(true);
    let result = null;
    try {
      if (createdProjectId) {
        result = await projectsAPI.update(createdProjectId, projectData);
        result = { ...result, project_id: createdProjectId, status: projectData.status };
      } else {
        result = await projectsAPI.create(projectData);
        setCreatedProjectId(result.project_id);
      }
      // Sync local state with what was persisted so the header (Expenses box,
      // Profit Margin) immediately reflects rows that were saved without
      // first being moved via "Save Expense".
      setSavedExpenses(projectData.expenses_list);
      setExpenseRows([{ expense: '', date: '', memo: '', amount: '', percent: '100' }]);
      toast.success('Project saved successfully');
      return result;
    } catch (error) {
      console.error('Error saving project:', error);
      const detail = error?.response?.data?.detail || error.message || 'An unknown error occurred while saving the project';
      showErrorModal(detail);
      return null;
    } finally {
      setSaving(false);
      savingRef.current = false;
    }
  };

  const handleSaveReturn = async () => {
    const result = await saveProject();
    if (result) {
      const internalStatus = result.status || 'open';
      if (internalStatus === 'ar') navigate('/projects/accounts-receivable');
      else if (internalStatus === 'closed') navigate('/projects/closed');
      else if (internalStatus === 'dead') navigate('/projects/dead');
      else navigate('/projects');
    }
  };

  const handleSaveRefresh = async () => {
    const isNew = !createdProjectId;
    const result = await saveProject();
    if (result) {
      if (isNew) {
        showFlashMessage('Project created with ID: ' + result.project_id);
      }
    }
  };

  return (
    <Layout>
      <div className="p-6 bg-gray-100 min-h-screen">
        {/* Header */}
        <div className="flex items-center justify-between mb-4">
          <div>
            {createdProjectId ? (
              <>
                <div className="text-[#5B8DB8] text-lg mb-1">
                  Project # {createdProjectId}
                  {(() => {
                    const cust = customers.find(c => c.customer_id === selectedCustomerId);
                    const ph = cust?.phone || cust?.primary_phone || '';
                    const em = cust?.email || cust?.primary_email || '';
                    return (
                      <>
                        {ph && <span className="text-gray-500 text-sm ml-2">(Ph {ph})</span>}
                        {em && <span className="text-gray-500 text-sm ml-2">(Em: {em})</span>}
                      </>
                    );
                  })()}
                </div>
                <h1 className="text-2xl text-[#5B8DB8] font-light">
                  {(() => {
                    const cust = customers.find(c => c.customer_id === selectedCustomerId);
                    return cust ? getCustomerDisplayName(cust) : '';
                  })()} - {address}
                </h1>
              </>
            ) : (
              <>
                <div className="text-[#5B8DB8] text-sm">Projects</div>
                <h1 className="text-2xl text-[#5B8DB8] font-light">Create Project</h1>
              </>
            )}
          </div>
          <div className="flex items-center gap-4">
            {(() => {
              const baseExpenses = savedExpenses.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
              const revenue = parseFloat(String(preTaxTotalValue).replace(/[$,]/g, '')) || 0;
              // Mirror ProjectDetail logic: include the Job Type's commission %
              // (skipped when Non-Commission Project is checked or revenue is blank).
              const typeCommissionPct = getJobTypeCommission(typeCommissionMap, projectType) || 0;
              const commission = (nonCommissionProject || revenue <= 0) ? 0 : revenue * (typeCommissionPct / 100);
              const totalExpenses = baseExpenses + commission;
              const profitMargin = revenue > 0 ? ((revenue - totalExpenses) / revenue * 100) : 0;
              const hourlyRate = 0; // no labor on create
              const laborHrs = 0;
              return (
                <>
                  <div className="text-center border rounded px-4 py-2 bg-white">
                    <div className="text-xs text-gray-500 mb-1">PROFIT MARGIN</div>
                    <div className="text-xl">{revenue > 0 ? `${profitMargin.toFixed(2)}%` : '---'}</div>
                  </div>
                  <div className="text-center border rounded px-4 py-2 bg-white">
                    <div className="text-xs text-gray-500 mb-1">HOURLY LABOR RATE</div>
                    <div className="text-xl">{laborHrs > 0 ? `$${(revenue / laborHrs).toFixed(2)}` : '---'}</div>
                  </div>
                  <div className="text-center border rounded px-4 py-2 bg-white">
                    <div className="text-xs text-gray-500 mb-1">LABOR HRS</div>
                    <div className="text-2xl font-light">{laborHrs.toFixed(2)} <span className="text-sm">HRS</span></div>
                  </div>
                  <div className="text-center border rounded px-4 py-2 bg-white">
                    <div className="text-xs text-gray-500 mb-1">EXPENSES</div>
                    <div className="text-2xl font-light">${totalExpenses.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</div>
                  </div>
                </>
              );
            })()}
          </div>
        </div>

        {/* Main Content */}
        <div className="flex gap-4">
          {/* Left Sidebar */}
          <div className="w-64 bg-white shadow-sm rounded">
            {/* Save Buttons */}
            <div className="p-4 flex gap-2">
              <button
                onClick={handleSaveReturn}
                disabled={saving}
                className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-4 py-2 rounded transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-return-btn"
              >
                {saving ? 'Saving…' : 'Save & Return'}
              </button>
              <button
                onClick={handleSaveRefresh}
                disabled={saving}
                className="border border-gray-300 hover:bg-gray-50 text-gray-600 px-4 py-2 rounded transition-colors text-sm disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="save-refresh-btn"
              >
                {saving ? 'Saving…' : 'Save & Refresh'}
              </button>
            </div>
            
            <nav className="py-2">
              <button
                onClick={() => setActiveTab('information')}
                className={`w-full text-left px-6 py-3 flex items-center gap-2 ${
                  activeTab === 'information'
                    ? 'bg-gray-100 text-red-500 border-l-4 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>&#9889;</span> Project Information
              </button>
              <button
                onClick={() => setActiveTab('personnel')}
                className={`w-full text-left px-6 py-3 flex items-center gap-2 ${
                  activeTab === 'personnel'
                    ? 'bg-gray-100 text-red-500 border-l-4 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>&#128100;</span> Key Personnel
              </button>
              <button
                onClick={() => setActiveTab('expenses')}
                className={`w-full text-left px-6 py-3 flex items-center gap-2 ${
                  activeTab === 'expenses'
                    ? 'bg-gray-100 text-red-500 border-l-4 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <DollarSign className="w-5 h-5 shrink-0 text-green-500" /> <span>Expenses</span>
              </button>
              <button
                onClick={() => setActiveTab('labor')}
                className={`w-full text-left px-6 py-3 flex items-center gap-2 ${
                  activeTab === 'labor'
                    ? 'bg-gray-100 text-red-500 border-l-4 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>&#128202;</span> Labor Summary
              </button>
              <button
                onClick={() => setActiveTab('notes')}
                className={`w-full text-left px-6 py-3 flex items-center gap-2 ${
                  activeTab === 'notes'
                    ? 'bg-gray-100 text-red-500 border-l-4 border-red-500'
                    : 'text-gray-600 hover:bg-gray-50'
                }`}
              >
                <span>&#128221;</span> Notes
              </button>
            </nav>
          </div>

          {/* Main Content Area */}
          <div className="flex-1 bg-white shadow-sm rounded p-6">
            {activeTab === 'information' && (
              <CreateInformationTab
                status={status} setStatus={setStatus} attempted={attempted}
                selectedCustomerId={selectedCustomerId} handleCustomerChange={handleCustomerChange}
                customers={customers} loadingCustomers={loadingCustomers}
                phone={phone} setPhone={setPhone}
                territory={territory} setTerritory={setTerritory}
                franchises={franchises}
                projectType={projectType} setProjectType={setProjectType}
                projectTypes={projectTypes}
                address={address} setAddress={setAddress}
                customerAddresses={customerAddresses} handleAddressChange={handleAddressChange}
                projectSource={projectSource} setProjectSource={setProjectSource}
                projectSources={projectSources}
                fnolDate={fnolDate} setFnolDate={setFnolDate}
                estimateDate={estimateDate} setEstimateDate={setEstimateDate}
                startDate={startDate} setStartDate={setStartDate}
                completionDate={completionDate} setCompletionDate={setCompletionDate}
                preTaxTotalValue={preTaxTotalValue} setPreTaxTotalValue={setPreTaxTotalValue}
                placeholder={placeholder} setPlaceholder={setPlaceholder}
                paymentType={paymentType} setPaymentType={setPaymentType}
                claimNumber={claimNumber} setClaimNumber={setClaimNumber}
                invoiceNumber={invoiceNumber} setInvoiceNumber={setInvoiceNumber}
                paidInFull={paidInFull} setPaidInFull={setPaidInFull}
                mgmtBonusPaid={mgmtBonusPaid} setMgmtBonusPaid={setMgmtBonusPaid}
                projectPaidDate={projectPaidDate} setProjectPaidDate={setProjectPaidDate}
                restorationOpp={restorationOpp} setRestorationOpp={setRestorationOpp}
                nonCommissionProject={nonCommissionProject} setNonCommissionProject={setNonCommissionProject}
                authorizationSigned={authorizationSigned} setAuthorizationSigned={setAuthorizationSigned}
                uploadRequired={uploadRequired} setUploadRequired={setUploadRequired}
                getCustomerDisplayName={getCustomerDisplayName}
                typeCommissionMap={typeCommissionMap}
              />
            )}

            {activeTab === 'personnel' && (
              <CreatePersonnelTab
                selectedProjectLead={selectedProjectLead} setSelectedProjectLead={setSelectedProjectLead} projectLeads={projectLeads}
                selectedEstimator={selectedEstimator} setSelectedEstimator={setSelectedEstimator} estimators={estimators}
                estimatorAction={estimatorAction} setEstimatorAction={setEstimatorAction}
                selectedMarketingRep={selectedMarketingRep} setSelectedMarketingRep={setSelectedMarketingRep} marketingReps={marketingReps}
                selectedOfficeContact={selectedOfficeContact} setSelectedOfficeContact={setSelectedOfficeContact} officeContacts={officeContacts}
                officeAction={officeAction} setOfficeAction={setOfficeAction}
                pmRows={pmRows} setPmRows={setPmRows}
                productionEmployees={productionEmployees}
                isDirty={!!(selectedProjectLead || selectedEstimator || selectedMarketingRep || selectedOfficeContact || pmRows.some(r => r.employee))}
                onSave={async () => { await saveProject(); }}
              />
            )}

            {activeTab === 'expenses' && (
              <CreateExpensesTab
                expenseRows={expenseRows}
                handleAddExpenseRow={handleAddExpenseRow}
                handleRemoveExpenseRow={handleRemoveExpenseRow}
                handleExpenseRowChange={handleExpenseRowChange}
                handleSaveExpenseRow={handleSaveExpenseRow}
                savedExpenses={savedExpenses}
                handleDeleteSavedExpense={handleDeleteSavedExpense}
                expenseOptions={expenseOptions}
                expenseSort={expenseSort} setExpenseSort={setExpenseSort}
                expenseSearch={expenseSearch} setExpenseSearch={setExpenseSearch}
                isProjectSaved={!!createdProjectId}
              />
            )}

            {activeTab === 'labor' && (
              <div>
                <div className="flex items-center justify-between mb-6">
                  <h2 className="text-2xl text-[#5B8DB8] font-light">Labor Summary</h2>
                  {createdProjectId ? (
                    <a
                      href={`/timeclock?project=${createdProjectId}`}
                      className="bg-[#4ECFDB] hover:bg-[#3AB8C5] text-white px-3 py-1.5 rounded text-sm flex items-center gap-2 transition-colors"
                      data-testid="goto-timeclock-create"
                    >
                      <Clock className="w-4 h-4" /> Go to Timeclock
                    </a>
                  ) : (
                    <span
                      className="bg-gray-300 text-white px-3 py-1.5 rounded text-sm flex items-center gap-2 cursor-not-allowed"
                      title="Save the project first to enable the Timeclock link"
                    >
                      <Clock className="w-4 h-4" /> Go to Timeclock
                    </span>
                  )}
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="text-left p-3 text-gray-600 font-normal">Employee</th>
                        <th className="text-left p-3 text-gray-600 font-normal">Hours</th>
                        <th className="text-left p-3 text-gray-600 font-normal">Amount</th>
                        <th className="text-left p-3 text-gray-600 font-normal">Vehicle Expense</th>
                        <th className="text-left p-3 text-gray-600 font-normal">Fuel Expense</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b">
                        <td className="p-3 text-gray-700"></td>
                        <td className="p-3 text-gray-700"></td>
                        <td className="p-3 text-gray-700">$0.00</td>
                        <td className="p-3 text-gray-700">$0.00</td>
                        <td className="p-3 text-gray-700">$0.00</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {activeTab === 'notes' && (
              <div>
                <div className="flex justify-between items-center mb-6">
                  <h2 className="text-2xl text-[#5B8DB8] font-light">Project Notes</h2>
                  <div className="flex items-center gap-2">
                    {createdProjectId && (
                      <>
                        <button
                          onClick={handleSaveNote}
                          disabled={!notes.trim()}
                          className={`px-4 py-2 rounded text-sm text-white ${notes.trim() ? 'bg-[#4ECFDB] hover:bg-[#3AB8C5]' : 'bg-gray-300 cursor-not-allowed'}`}
                          data-testid="save-note-btn"
                        >
                          Save Note
                        </button>
                        <button
                          onClick={() => { setNotes(''); setNoteUrgent(false); }}
                          className="border border-gray-300 hover:bg-gray-100 text-gray-600 px-4 py-2 rounded text-sm"
                        >
                          Cancel
                        </button>
                      </>
                    )}
                    <label className="flex items-center gap-1.5 text-sm text-gray-600 ml-2 cursor-pointer">
                      <input type="checkbox" checked={noteUrgent} onChange={(e) => setNoteUrgent(e.target.checked)} className="w-4 h-4" data-testid="note-urgent-checkbox" />
                      Mark as Urgent
                    </label>
                  </div>
                </div>
                
                <textarea
                  placeholder="Initial project notes"
                  value={notes}
                  onChange={(e) => setNotes(e.target.value)}
                  className="w-full h-64 border border-gray-300 rounded p-4 text-gray-600 resize-y"
                  data-testid="note-textarea"
                />
                {!createdProjectId && notes.trim() && (
                  <p className="text-xs text-gray-400 mt-1">This note will be saved when you save the project.</p>
                )}

                {savedNotesList.length > 0 && (
                  <div className="mt-6">
                    <h3 className="text-sm text-gray-500 mb-2">Saved Notes</h3>
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="border-b-2">
                          <th className="text-left p-3 text-gray-600 font-normal w-28">Created By</th>
                          <th className="text-left p-3 text-gray-600 font-normal w-28">Created On</th>
                          <th className="text-left p-3 text-gray-600 font-normal">Notes</th>
                        </tr>
                      </thead>
                      <tbody>
                        {savedNotesList.map((note, idx) => {
                          const parts = (note.created_on || '').split(' ');
                          const datePart = parts[0] || '';
                          const timePart = parts.slice(1).join(' ') || '';
                          return (
                            <tr key={idx} className={`border-b align-top ${note.urgent ? 'text-red-600' : ''}`} data-testid={`saved-note-row-${idx}`}>
                              <td className={`p-3 ${note.urgent ? 'text-red-600' : 'text-gray-700'}`}>{note.created_by}</td>
                              <td className={`p-3 ${note.urgent ? 'text-red-600' : 'text-gray-700'}`}>
                                <div>{datePart}</div>
                                {timePart && <div className={note.urgent ? 'text-red-400' : 'text-gray-500'}>{timePart}</div>}
                              </td>
                              <td className={`p-3 ${note.urgent ? 'text-red-600 font-semibold' : 'text-gray-700'}`}><p>{note.text}</p></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CreateProject;
